/** @format */

import { memo } from 'react'
import { CopyBlock, ocean } from 'react-code-blocks'

import styles from 'dir/styles/courses.module.css'
import booleanOperatorsImg from 'assets/images/booleanOperators.png'
import queueImg from 'assets/images/queue.png'

export const _10 = memo(() => (
  <section className={styles.section}>
    <h2 className={styles.h2}>1 - Двоичная логика</h2>
    <p className={styles.description}>
      Любая программа может быть создана на основе нескольких принципов: последовательности выполнения и ветвлений.
    </p>
    <p className={styles.description}>
      <b>Последовательность выполнения</b> — следование записи кода сверху вниз. Но не всегда программа должна
      выполняться строго последовательно. Для изменения последовательности используются циклы и ветвления.
    </p>
    <p className={styles.description}>
      <b>Ветвление</b> — возможность выполнить ту или иную последовательность кода в зависимости от условия. Условие
      может быть любым, но результат его проверки всегда будет одним из двух значений{' '}
      <code className={styles.code}>true</code> или <code className={styles.code}>false</code>.
    </p>
    <p className={styles.description}>
      Компьютер использует бинарный (от латинского bis - дважды) код. Т.е. всего два значения используются для создания
      любых программ: <code className={styles.code}>0</code> и <code className={styles.code}>1</code>. Это значит, что
      условия также задаются в виде <code className={styles.code}>0</code> (нет, false) и{' '}
      <code className={styles.code}>1</code> (да, true).
    </p>
    <p className={styles.description}>
      В математике существует раздел булевой логики, в котором условия бинарны, т.е. могут быть представлены в виде{' '}
      <code className={styles.code}>0</code> и <code className={styles.code}>1</code>, а также в виде слов{' '}
      <code className={styles.code}>true</code> и <code className={styles.code}>false</code>. Именно эта логика
      используется для реализации ветвления.
    </p>

    <section className={styles.subsection}>
      <h3 className={styles.h3}>1.1 - Приведение типов</h3>
      <p className={styles.description}>
        Для того чтобы работала булева логика необходимо на входе иметь два значения. Поэтому в JavaScript, в логических
        операциях, осуществляется приведение типов операндов к <code className={styles.code}>true</code> или{' '}
        <code className={styles.code}>false</code>. Приведение происходит если в коде обнаружен логический оператор.
      </p>
      <p className={styles.description}>
        <b>Truthy</b> и <b>Falsy</b> — термины, которые используются для тех значений, которые, в логической операции,
        приводятся к <code className={styles.code}>true</code> или <code className={styles.code}>false</code>, хотя
        изначально не были булями.
      </p>
      <p className={styles.notificationStudy}>
        Запомните 6 ложных (falsy) значений, приводящихся к<code className={styles.codeError}>false</code> в логическом
        преобразовании: <code className={styles.codeError}>0</code>, <code className={styles.codeError}>NaN</code>,{' '}
        <code className={styles.codeError}>null</code>, <code className={styles.codeError}>undefined</code>, пустая
        строка: <code className={styles.codeError}>""</code> или <code className={styles.codeError}>''</code>,{' '}
        <code className={styles.codeError}>false</code>. Абсолютно все остальное приводится к{' '}
        <code className={styles.codeError}>true</code>.
      </p>
    </section>

    <section className={styles.subsection}>
      <h2 className={styles.h2}>2 - Логические операторы</h2>
      <p className={styles.description}>
        Есть три логических оператора, которые используются для проверки выполнения множественных выражений.
      </p>
      <img className={styles.img_without_shadow} src={booleanOperatorsImg} alt="booleanOperatorsImg" />

      <section className={styles.subsection}>
        <h3 className={styles.h3}>2.1 - Логическое «И»</h3>
        <p className={styles.description}>
          Оператор <code className={styles.code}>&amp;&amp;</code> приводит все операнды к булю и возвращает одно из
          значений (операндов). Левый операнд если его можно привести к <code className={styles.code}>false</code>, и
          правый в остальных случаях.
        </p>
        <CopyBlock
          text={`const num = 20;
const result = num > 10 && num < 30;
console.log(result); // true`}
          language="js"
          showLineNumbers={false}
          theme={ocean}
        />
        <p className={styles.description}>
          В коде выше мы проверяем условие: переменная <code className={styles.code}>num</code> больше{' '}
          <code className={styles.code}>10</code> и меньше <code className={styles.code}>30</code>. Так как оба условия
          вернут <code className={styles.code}>true</code>, то и результатом всего выражения будет
          <code className={styles.code}>true</code>.
        </p>
        <p className={styles.description}>
          Для того чтобы оператор <code className={styles.code}>&amp;&amp;</code> вернул{' '}
          <code className={styles.code}>true</code>, требуется чтобы все операнды были истинными (truthy). Если хотя бы
          один из операндов будет приведен к <code className={styles.code}>false</code>, то результатом выражения будет
          этот операнд.
        </p>
        <CopyBlock
          text={`const num = 40;
const result = num > 10 && num < 30;
console.log(result); // false`}
          language="js"
          showLineNumbers={false}
          theme={ocean}
        />
      </section>

      <section className={styles.subsection}>
        <h3 className={styles.h3}>2.2 - Логическое «ИЛИ»</h3>
        <p className={styles.description}>
          Оператор <code className={styles.code}>||</code> возвращает одно из значений (операндов). Левый операнд если
          его можно привести к <code className={styles.code}>true</code>, и правый в остальных случаях.
        </p>
        <CopyBlock
          text={`const num = 5;
const result = num < 10 || num > 30;
console.log(result); // true`}
          language="js"
          showLineNumbers={false}
          theme={ocean}
        />
        <p className={styles.description}>
          Это тоже будет <code className={styles.code}>true</code> так как хотя бы один из операндов был приведен к{' '}
          <code className={styles.code}>true</code>.
        </p>
        <CopyBlock
          text={`const num = 40;
const result = num < 10 || num > 30;
console.log(result); // true`}
          language="js"
          showLineNumbers={false}
          theme={ocean}
        />
        <p className={styles.description}>
          А тут ни одно из условий не выполняется, поэтому получаем <code className={styles.code}>false</code>.
        </p>
        <CopyBlock
          text={`const num = 20;
const result = num < 10 || num > 30;
console.log(result); // false`}
          language="js"
          showLineNumbers={false}
          theme={ocean}
        />
      </section>

      <section className={styles.subsection}>
        <h3 className={styles.h3}>2.3 - Логическое «НЕ»</h3>
        <p className={styles.description}>
          Оператор <code className={styles.code}>!</code> приводит операнд к булю, если необходимо, а затем заменяет его
          на противоположный.
        </p>
        <CopyBlock
          text={`console.log(!true); // false
console.log(!false); // true
console.log(!1); // false
console.log(!0); // true`}
          language="js"
          showLineNumbers={false}
          theme={ocean}
        />
      </section>

      <section className={styles.subsection}>
        <h3 className={styles.h3}>2.4 - Порядок вычисления</h3>
        <p className={styles.description}>При выполнении логических операций, правый операнд может не вычисляться.</p>
        <img className={styles.img_without_shadow} src={queueImg} alt="queue" />
        <p className={styles.description}>
          <b>"Купить билет И отдохнуть"</b> - если левый операнд <code className={styles.code}>"Купить билет"</code>{' '}
          окажется
          <code className={styles.code}>false</code>, то вычислять второй нет смысла.
        </p>
        <CopyBlock
          text={`false && (этот операнд не вычисляется)`}
          language="js"
          showLineNumbers={false}
          theme={ocean}
        />
        <p className={styles.description}>
          <b>"Отдохнуть ИЛИ сохранить деньги"</b> - если левый операнд <code className={styles.code}>"Отдохнуть"</code>{' '}
          окажется
          <code className={styles.code}>true</code>, то вычислять второй нет смысла.
        </p>
        <CopyBlock text={`true || (этот операнд не вычисляется)`} language="js" showLineNumbers={false} theme={ocean} />
      </section>

      <section className={styles.subsection}>
        <h2 className={styles.h2}>3 - Дополнительные материалы</h2>
        <ul className={styles.list}>
          <li className={styles.list__item}>
            <a
              className={styles.link}
              rel="noopener noreferrer"
              href="https://blog.usejournal.com/mastering-javascripts-and-logical-operators-fd619b905c8f"
              target="_blank"
            >
              Mastering JavaScript’s &amp;&amp; and || logical operators
            </a>
          </li>
          <li className={styles.list__item}>
            <a
              className={styles.link}
              rel="noopener noreferrer"
              href="https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Operators/Logical_Operators"
              target="_blank"
            >
              MDN: Логические операторы
            </a>
          </li>
          <li className={styles.list__item}>
            <a className={styles.link} rel="noopener noreferrer" href="https://youtu.be/UvI-AMAtrvE" target="_blank">
              Logic Gates Explained (видео)
            </a>
          </li>
        </ul>
      </section>
    </section>
  </section>
))
